import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  InputAdornment,
} from "@mui/material";
import { DraggablePaper } from "./DraggablePaper";
import { useDebouncedCallback } from "../utils/useDebouncedCallback";

// Memoize RadiusDialog to prevent unnecessary re-renders
const RadiusDialog = React.memo(({ open, onClose, canvas, selectedObject }) => {
  const [radius, setRadius] = useState(0);
  const [prevRadius, setPrevRadius] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [inputRadius, setInputRadius] = useState("");
  const selectedObjectRef = useRef(selectedObject);

  // Update ref when selectedObject changes
  useEffect(() => {
    selectedObjectRef.current = selectedObject;
    console.log("[RadiusDialog] selectedObject updated:", selectedObject);
  }, [selectedObject]);

  // Initialize radius value when dialog opens or selected object changes
  useEffect(() => {
    if (selectedObject && open && selectedObject.type === "circle") {
      const currentRadius = Math.round(
        Number(selectedObject.get("radius")) || 0
      );
      setRadius(currentRadius);
      setInputRadius(currentRadius.toString());
      setPrevRadius(currentRadius);
      setIsDirty(false);
      console.log("[RadiusDialog] Initialized radius:", currentRadius);
    }
  }, [selectedObject, open]);

  // Memoize the update callback
  const updateRadiusCallback = useCallback(
    (value) => {
      const roundedValue = Number(value);
      setRadius(roundedValue);
      setInputRadius(roundedValue.toString());
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ radius: roundedValue });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[RadiusDialog] Updated radius:",
          roundedValue,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[RadiusDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  // Create debounced update function
  const updateRadius = useDebouncedCallback(updateRadiusCallback, 100, canvas);

  // Handle dialog close (cancel)
  const handleCancel = useCallback(() => {
    if (isDirty && selectedObject) {
      selectedObject.set({ radius: prevRadius });
      selectedObject.setCoords();
      canvas?.requestRenderAll();
      console.log("[RadiusDialog] Canceled, reverted to radius:", prevRadius);
    }
    onClose();
  }, [isDirty, selectedObject, prevRadius, canvas, onClose]);

  // Handle apply radius (save changes and add to history)
  const handleApply = useCallback(() => {
    updateRadius.flush(); // Ensure pending updates are applied
    if (isDirty && canvas && selectedObject) {
      canvas.setActiveObject(selectedObject);
      selectedObject.set({ radius });
      selectedObject.setCoords();
      canvas.requestRenderAll();
      canvas.fire("object:modified", { target: selectedObject });
      console.log(
        "[RadiusDialog] Applied radius:",
        radius,
        "Fired object:modified for:",
        selectedObject
      );
    } else {
      console.log(
        "[RadiusDialog] Apply skipped: isDirty=",
        isDirty,
        "canvas=",
        !!canvas,
        "selectedObject=",
        !!selectedObject
      );
    }
    setIsDirty(false);
    onClose();
  }, [isDirty, canvas, selectedObject, radius, updateRadius, onClose]);

  // Stop mouse events from propagating to allow input interaction
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent backdrop click from closing the dialog
  const handleBackdropClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent keydown events (e.g., Escape key) from closing the dialog
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
    }
  }, []);

  // Handle input changes
  const handleRadiusChange = useCallback((e) => {
    setInputRadius(e.target.value);
  }, []);

  const handleRadiusBlur = useCallback(() => {
    const value = Number(inputRadius);
    if (!isNaN(value) && value >= 0) {
      updateRadius(value);
    } else {
      setInputRadius(radius.toString());
    }
  }, [inputRadius, radius, updateRadius]);

  const handleRadiusStep = useCallback(
    (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= 0) {
        updateRadius(value);
      }
    },
    [updateRadius]
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCancel();
      }}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
        onClick: handleBackdropClick,
      }}
      onKeyDown={handleKeyDown}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          color: "#ffffff",
          backgroundColor: "#0d2445",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
          cursor: "move",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "8px" }}
        >
          <circle cx="4" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
        </svg>
        Radius Toolbar
      </DialogTitle>
      <DialogContent
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Stack direction="column" sx={{ marginTop: 1 }}>
          <TextField
            type="number"
            id="canvasRadius"
            margin="dense"
            value={inputRadius}
            onChange={handleRadiusChange}
            onBlur={handleRadiusBlur}
            onInput={handleRadiusStep}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
              inputProps: { step: 1, min: 0 }, // Step 1px for fine control
            }}
            label="Radius"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Radius in pixels" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Button
          onClick={handleCancel}
          color="secondary"
          aria-label="Cancel radius changes"
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          aria-label="Apply radius changes"
        >
          Apply Radius
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default RadiusDialog;
